import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import * as lodash from 'lodash';
import * as React from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { EUnitOfMeasureMetricLabel } from '../../conversion/enum';
import Input from '../../shared/component/Input';
import { EDrainageCapacityType, IDrainageCapacityProduct } from '../../shared/iProject';
import variable from '../../shared/style/variable';

interface IModalDrainageCapacityProps {
    drainageCapacities: IDrainageCapacityProduct[];
    open: boolean;
    onClose: () => void;
    onApply: (drainageCapacities: IDrainageCapacityProduct[]) => void;
}

enum ETableDrainageCapacityValue {
    IslColumns,
    AppliedLoadsRows,
    Values,
}

enum ETableAction {
    AddRow,
    RemoveRow,
    AddColumn,
    RemoveColumn,
}

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 900,
};

const ModalDrainageCapacity = (props: IModalDrainageCapacityProps) => {
    const { t } = useTranslation();

    const [drainageCapacityVertical, setDrainageCapacityVertical] = React.useState<IDrainageCapacityProduct>(props.drainageCapacities.find(x => x.position == EDrainageCapacityType.Vertical)!);
    const [drainageCapacityHorizontal, setDrainageCapacityHorizontal] = React.useState<IDrainageCapacityProduct>(props.drainageCapacities.find(x => x.position == EDrainageCapacityType.Horizontal)!);

    const [errorMessageHorizontal, setErrorMessageHorizontal] = React.useState('');
    const [errorMessageVertical, setErrorMessageVertical] = React.useState('');

    const handleChangeDrainageCapacity = (type: EDrainageCapacityType, tableDrainageCapacityValue: ETableDrainageCapacityValue, value: string, indexRow?: number, indexColumn?: number) => {
        let drainageCapacity, setDrainageCapacity;
        if (type == EDrainageCapacityType.Horizontal) {
            drainageCapacity = drainageCapacityHorizontal;
            setDrainageCapacity = setDrainageCapacityHorizontal;
        } else {
            drainageCapacity = drainageCapacityVertical;
            setDrainageCapacity = setDrainageCapacityVertical;
        }

        let newDrainageCapacity = lodash.cloneDeep(drainageCapacity)!;
        let t = parseFloat(value);
        switch (tableDrainageCapacityValue) {
            case ETableDrainageCapacityValue.IslColumns:
                newDrainageCapacity!.islColumns[indexColumn!] = !isNaN(t) ? t : null;
                break;
            case ETableDrainageCapacityValue.AppliedLoadsRows:
                newDrainageCapacity!.appliedLoadsRows[indexRow!] = !isNaN(t) ? t : null;
                break;
            case ETableDrainageCapacityValue.Values:
                newDrainageCapacity!.values[indexRow!][indexColumn!] = !isNaN(t) ? t : null;
                break;
            default:
                break;
        }
        setDrainageCapacity(newDrainageCapacity);
    };

    const handleTableAction = (type: EDrainageCapacityType, actionType: ETableAction) => {
        let drainageCapacity, setDrainageCapacity;
        if (type == EDrainageCapacityType.Horizontal) {
            drainageCapacity = drainageCapacityHorizontal;
            setDrainageCapacity = setDrainageCapacityHorizontal;
        } else {
            drainageCapacity = drainageCapacityVertical;
            setDrainageCapacity = setDrainageCapacityVertical;
        }

        let newDrainageCapacity = lodash.cloneDeep(drainageCapacity)!;
        switch (actionType) {
            case ETableAction.AddColumn:
                newDrainageCapacity!.islColumns.push(drainageCapacity!.islColumns![drainageCapacity!.islColumns.length! - 1]! + 0.2);
                newDrainageCapacity!.values.map(x => x.push(null));
                break;
            case ETableAction.RemoveColumn:
                newDrainageCapacity!.islColumns.pop();
                newDrainageCapacity!.values.map(x => x.pop());
                break;
            case ETableAction.AddRow:
                newDrainageCapacity!.appliedLoadsRows.push(drainageCapacity!.appliedLoadsRows![drainageCapacity!.appliedLoadsRows.length! - 1]! + 200);
                newDrainageCapacity!.values.push(drainageCapacity!.islColumns.map(x => null)!);
                break;
            case ETableAction.RemoveRow:
                newDrainageCapacity!.appliedLoadsRows.pop();
                newDrainageCapacity!.values.pop();
                break;
            default:
                break;
        }
        setDrainageCapacity(newDrainageCapacity);
    };

    const handleClickApply = () => {
        if (drainageCapacityHorizontal!.islColumns.some(x => x == null) || drainageCapacityHorizontal!.appliedLoadsRows.some(x => x == null)) {
            setErrorMessageHorizontal(t('drainageCapacityErrorMessage'));
        } else if (drainageCapacityVertical!.islColumns.some(x => x == null) || drainageCapacityVertical!.appliedLoadsRows.some(x => x == null)) {
            setErrorMessageVertical(t('drainageCapacityErrorMessage'));
        } else {
            props.onApply([drainageCapacityVertical, drainageCapacityHorizontal]!);
        }
    };

    return (
        <Modal open={props.open} onClose={props.onClose} aria-labelledby='modal-drainageCapacity-title' aria-describedby='modal-drainageCapacity-description'>
            <Box mx={style}>
                <Draggable cancel='.btn'>
                    <Stack variant='modal' height='100%' direction='column' spacing={4}>
                        <Stack variant='bottomLine' direction={'column'} spacing={2}>
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <Typography variant='h5' gutterBottom color={variable.greenLight}>
                                    {t('changeDrainageCapacity')}
                                </Typography>
                                <IconButton aria-label='close-modal' onClick={props.onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                        <Stack flexGrow={1} spacing={2}>
                            <Stack flexGrow={1} position={'relative'}>
                                <Stack variant='scrollable'>
                                    {[drainageCapacityVertical, drainageCapacityHorizontal].map(d => (
                                        <Accordion disableGutters>
                                            <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                <Typography>{d.position == EDrainageCapacityType.Vertical ? t('drainageCapacityVertical') : t('drainageCapacityHorizontal')}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack>
                                                    <Stack direction={'row'}>
                                                        <TableContainer>
                                                            <Table size='small' aria-label='a dense table'>
                                                                <TableHead>
                                                                    <TableRow sx={{ 'td, th': { border: 2 } }}>
                                                                        <TableCell>{t('Isl')}</TableCell>
                                                                        {d!.islColumns?.map((x, i) => (
                                                                            <TableCell key={x} align='right'>
                                                                                <Input
                                                                                    isNumber
                                                                                    value={x}
                                                                                    onChange={(e: any) =>
                                                                                        handleChangeDrainageCapacity(d.position, ETableDrainageCapacityValue.IslColumns, e.target.value, i)
                                                                                    }
                                                                                />
                                                                            </TableCell>
                                                                        ))}
                                                                    </TableRow>
                                                                    <TableRow sx={{ border: 2 }}>
                                                                        <TableCell>{t('appliedLoads', { unitOfMeasure: EUnitOfMeasureMetricLabel.kiloPascal })}</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {d!.appliedLoadsRows?.map((x, i) => (
                                                                        <TableRow key={x} sx={{ 'td, th': { border: 2 } }}>
                                                                            <TableCell component='th' scope='row'>
                                                                                <Input
                                                                                    isNumber
                                                                                    value={x}
                                                                                    onChange={(e: any) =>
                                                                                        handleChangeDrainageCapacity(d.position, ETableDrainageCapacityValue.AppliedLoadsRows, e.target.value, i)
                                                                                    }
                                                                                />
                                                                            </TableCell>
                                                                            {d!.islColumns.map((y, u) => (
                                                                                <TableCell key={y} align='right'>
                                                                                    <Input
                                                                                        isNumber
                                                                                        value={d!.values[i][u]}
                                                                                        onChange={(e: any) =>
                                                                                            handleChangeDrainageCapacity(d.position, ETableDrainageCapacityValue.Values, e.target.value, i, u)
                                                                                        }
                                                                                    />
                                                                                </TableCell>
                                                                            ))}
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                        <Stack alignSelf={'center'} direction={'row'}>
                                                            {d!.islColumns.length! < 6 && (
                                                                <IconButton aria-label='add-column' onClick={() => handleTableAction(d.position, ETableAction.AddColumn)}>
                                                                    <AddIcon />
                                                                </IconButton>
                                                            )}
                                                            {d!.islColumns.length! > 1 && (
                                                                <IconButton aria-label='remove-column' onClick={() => handleTableAction(d.position, ETableAction.RemoveColumn)}>
                                                                    <HorizontalRuleIcon />
                                                                </IconButton>
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                    <Stack alignSelf={'center'} direction={'row'}>
                                                        {d!.appliedLoadsRows.length! < 6 && (
                                                            <IconButton aria-label='add-row' onClick={() => handleTableAction(d.position, ETableAction.AddRow)}>
                                                                <AddIcon />
                                                            </IconButton>
                                                        )}
                                                        {d!.appliedLoadsRows.length! > 1 && (
                                                            <IconButton aria-label='remove-row' onClick={() => handleTableAction(d.position, ETableAction.RemoveRow)}>
                                                                <HorizontalRuleIcon />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                    <Stack>
                                                        <Typography color={'red'}>{d.position == EDrainageCapacityType.Vertical ? errorMessageVertical : errorMessageHorizontal}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            </Stack>
                            <Stack direction={'row'} spacing={5} justifyContent={'flex-end'}>
                                <Button className='btn' variant='white' onClick={props.onClose}>
                                    {t('close')}
                                </Button>
                                <Button className='btn' variant='orange' onClick={handleClickApply}>
                                    {t('apply')}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Draggable>
            </Box>
        </Modal>
    );
};

export default ModalDrainageCapacity;
